import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { 
  ArrowLeft, RefreshCw, Gift, Plus, Trash2, Edit, Save, X,
  Percent, DollarSign, Calendar, Users, Tag
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCoupons = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [packages, setPackages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_purchase: 0,
    max_uses: null,
    valid_until: '',
    active: true,
    description: '',
    applies_to: 'all',
    specific_package_ids: []
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadCoupons();
    loadPackages();
  }, [navigate]);

  const loadPackages = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/packages/?active_only=true`);
      const data = Array.isArray(res.data) ? res.data : [];
      setPackages(data.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR')));
    } catch (error) {
      console.error('Erro ao carregar pacotes:', error);
    }
  };

  const loadCoupons = async () => {
    try {
      setLoading(true);
      
      // Buscar cupons e reservas em paralelo
      const [couponsRes, reservationsRes, manualRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/coupons/`),
        axios.get(`${BACKEND_URL}/api/reservations/`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/manual-reservations/`).catch(() => ({ data: [] }))
      ]);
      
      const allReservations = [...(reservationsRes.data || []), ...(manualRes.data || [])];
      
      // Contar usos reais de cada cupom baseado nas reservas
      const usageCount = {};
      allReservations.forEach(res => {
        const couponCode = res.coupon_code || res.discount_coupon;
        if (couponCode) {
          const code = couponCode.toUpperCase();
          usageCount[code] = (usageCount[code] || 0) + 1;
        }
      });
      
      // Atualizar cupons com a contagem real
      const couponsWithRealUsage = couponsRes.data.map(coupon => ({
        ...coupon,
        real_uses_count: usageCount[coupon.code.toUpperCase()] || 0
      }));
      
      setCoupons(couponsWithRealUsage);
    } catch (error) {
      console.error('Erro ao carregar cupons:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar cupons.' });
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Sem limite';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: 10,
      min_purchase: 0,
      max_uses: null,
      valid_until: '',
      active: true,
      description: '',
      applies_to: 'all',
      specific_package_ids: [],
      partner_cpf: '',
      partner_name: '',
      commission_percentage: ''
    });
    setEditingCoupon(null);
  };

  const openModal = (coupon = null) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setFormData({
        code: coupon.code,
        discount_type: coupon.discount_type,
        discount_value: coupon.discount_value,
        min_purchase: coupon.min_purchase || 0,
        max_uses: coupon.max_uses,
        valid_until: coupon.valid_until ? coupon.valid_until.split('T')[0] : '',
        active: coupon.active,
        description: coupon.description || '',
        applies_to: coupon.applies_to || 'all',
        specific_package_ids: coupon.specific_package_ids || (coupon.specific_package_id ? [coupon.specific_package_id] : []),
        partner_cpf: coupon.partner_cpf || '',
        partner_name: coupon.partner_name || '',
        commission_percentage: coupon.commission_percentage != null ? coupon.commission_percentage : ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.code) {
        setMessage({ type: 'error', text: 'Código do cupom é obrigatório.' });
        return;
      }
      
      const data = {
        ...formData,
        code: formData.code.toUpperCase(),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
        specific_package_ids: formData.applies_to === 'specific' ? formData.specific_package_ids : [],
        partner_cpf: (formData.partner_cpf || '').replace(/\D/g, '') || null,
        partner_name: formData.partner_name || null,
        commission_percentage: formData.commission_percentage !== '' && formData.commission_percentage !== null ? parseFloat(formData.commission_percentage) : null
      };
      
      if (editingCoupon) {
        await axios.put(`${BACKEND_URL}/api/coupons/${editingCoupon.code}`, data);
        setMessage({ type: 'success', text: 'Cupom atualizado com sucesso!' });
      } else {
        await axios.post(`${BACKEND_URL}/api/coupons/`, data);
        setMessage({ type: 'success', text: 'Cupom criado com sucesso!' });
      }
      
      setShowModal(false);
      resetForm();
      await loadCoupons();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erro ao salvar cupom.' });
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Excluir cupom ${code}?`)) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/coupons/${code}`);
      setMessage({ type: 'success', text: 'Cupom excluído!' });
      await loadCoupons();
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao excluir cupom.' });
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await axios.put(`${BACKEND_URL}/api/coupons/${coupon.code}`, {
        active: !coupon.active
      });
      await loadCoupons();
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao alterar status.' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="hover:text-[#FFD700]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <Gift className="w-6 h-6" />
            <h1 className="text-xl font-bold">Cupons de Desconto</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={loadCoupons} variant="outline" size="sm" className="text-white border-white hover:bg-white/10">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            <Button onClick={() => openModal()} className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-900">
              <Plus className="w-4 h-4 mr-2" />
              Novo Cupom
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Mensagem */}
        {message.text && (
          <div className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })} className="ml-4">×</button>
          </div>
        )}

        {/* Lista de Cupons */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Cupons Cadastrados ({coupons.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {coupons.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Gift className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Nenhum cupom cadastrado.</p>
                <Button onClick={() => openModal()} className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeiro Cupom
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {coupons.map(coupon => (
                  <div 
                    key={coupon.code}
                    className={`p-4 rounded-lg border ${coupon.active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-300'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                          {coupon.discount_type === 'percentage' ? (
                            <Percent className="w-6 h-6 text-purple-600" />
                          ) : (
                            <DollarSign className="w-6 h-6 text-purple-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-lg">{coupon.code}</span>
                            {!coupon.active && <Badge className="bg-gray-200 text-gray-600">Inativo</Badge>}
                            {coupon.applies_to && coupon.applies_to !== 'all' && coupon.applies_to !== 'specific' && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">
                                {coupon.applies_to === 'internacional-aereo' ? 'Internacional' :
                                 coupon.applies_to === 'nacional-aereo' ? 'Nacional' :
                                 coupon.applies_to === 'rodoviario' ? 'Rodoviário' :
                                 coupon.applies_to === 'passeio-1-dia' ? '1 Dia' : coupon.applies_to}
                              </Badge>
                            )}
                            {coupon.applies_to === 'specific' && (
                              <Badge className="bg-purple-100 text-purple-700 text-xs">
                                {(coupon.specific_package_ids || []).length || 1} pacote(s)
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {coupon.discount_type === 'percentage' 
                              ? `${coupon.discount_value}% de desconto`
                              : `${formatPrice(coupon.discount_value)} de desconto`
                            }
                            {coupon.description && ` • ${coupon.description}`}
                          </p>
                          {coupon.applies_to === 'specific' && coupon.specific_package_ids?.length > 0 && (
                            <p className="text-xs text-purple-600 mt-0.5">
                              {coupon.specific_package_ids.map(id => {
                                const pkg = packages.find(p => p.id === id);
                                return pkg ? pkg.name : id;
                              }).join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Usos</p>
                          <p className="font-semibold">
                            {coupon.real_uses_count || 0}
                            {coupon.max_uses && <span className="text-gray-400">/{coupon.max_uses}</span>}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Validade</p>
                          <p className="font-semibold text-sm">{formatDate(coupon.valid_until)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch 
                            checked={coupon.active} 
                            onCheckedChange={() => toggleActive(coupon)}
                          />
                          <Button size="sm" variant="outline" onClick={() => openModal(coupon)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(coupon.code)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal de Criação/Edição */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCoupon ? '✏️ Editar Cupom' : '🎁 Novo Cupom'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código do Cupom *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="EX: DESCONTO10"
                  disabled={!!editingCoupon}
                />
              </div>
              <div>
                <Label>Tipo de Desconto</Label>
                <Select value={formData.discount_type} onValueChange={(v) => setFormData(prev => ({ ...prev, discount_type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">% Porcentagem</SelectItem>
                    <SelectItem value="fixed">R$ Valor Fixo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valor do Desconto *</Label>
                <Input
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))}
                  placeholder={formData.discount_type === 'percentage' ? '10' : '100'}
                />
              </div>
              <div>
                <Label>Compra Mínima (R$)</Label>
                <Input
                  type="number"
                  value={formData.min_purchase}
                  onChange={(e) => setFormData(prev => ({ ...prev, min_purchase: parseFloat(e.target.value) || 0 }))}
                  placeholder="0"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Máximo de Usos</Label>
                <Input
                  type="number"
                  value={formData.max_uses || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_uses: e.target.value ? parseInt(e.target.value) : null }))}
                  placeholder="Ilimitado"
                />
              </div>
              <div>
                <Label>Válido Até</Label>
                <Input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Descrição (opcional)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ex: Promoção de Ano Novo"
              />
            </div>
            
            <div>
              <Label>Aplicável a</Label>
              <Select 
                value={formData.applies_to} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, applies_to: v, specific_package_ids: v !== 'specific' ? [] : prev.specific_package_ids }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Pacotes</SelectItem>
                  <SelectItem value="specific">Pacotes Específicos</SelectItem>
                  <SelectItem value="internacional-aereo">Apenas Internacional (Aéreo)</SelectItem>
                  <SelectItem value="nacional-aereo">Apenas Nacional (Aéreo)</SelectItem>
                  <SelectItem value="rodoviario">Apenas Rodoviário</SelectItem>
                  <SelectItem value="passeio-1-dia">Apenas Passeio de 1 Dia</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.applies_to === 'specific' 
                  ? 'Selecione abaixo os pacotes onde este cupom será válido'
                  : 'O cupom só será válido para pacotes da categoria selecionada'}
              </p>
            </div>
            
            {/* Seleção de pacotes específicos */}
            {formData.applies_to === 'specific' && (
              <div>
                <Label>Pacotes Permitidos</Label>
                <div className="mt-2 border rounded-lg p-3 max-h-48 overflow-y-auto space-y-1.5 bg-gray-50">
                  {packages.map(pkg => {
                    const isSelected = formData.specific_package_ids.includes(pkg.id);
                    return (
                      <label 
                        key={pkg.id} 
                        className={`flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-white transition-colors ${isSelected ? 'bg-blue-50 border border-blue-200' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({ ...prev, specific_package_ids: [...prev.specific_package_ids, pkg.id] }));
                            } else {
                              setFormData(prev => ({ ...prev, specific_package_ids: prev.specific_package_ids.filter(id => id !== pkg.id) }));
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-800">{pkg.name}</span>
                      </label>
                    );
                  })}
                </div>
                {formData.specific_package_ids.length > 0 && (
                  <p className="text-xs text-blue-600 mt-1 font-medium">
                    {formData.specific_package_ids.length} pacote(s) selecionado(s)
                  </p>
                )}
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <Switch 
                checked={formData.active} 
                onCheckedChange={(v) => setFormData(prev => ({ ...prev, active: v }))}
              />
              <Label>Cupom Ativo</Label>
            </div>

            {/* Programa de Parceiros / Afiliados */}
            <div className="border-t pt-4 mt-2">
              <h4 className="font-semibold text-gray-900 mb-1">Programa de Parceiros (opcional)</h4>
              <p className="text-xs text-gray-500 mb-3">Vincule este cupom a um parceiro por CPF. Ele poderá acompanhar as comissões no dashboard do cliente.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>CPF do Parceiro</Label>
                  <Input
                    data-testid="partner-cpf-input"
                    value={formData.partner_cpf}
                    onChange={(e) => setFormData(prev => ({ ...prev, partner_cpf: e.target.value }))}
                    placeholder="Só números"
                    maxLength={14}
                  />
                </div>
                <div>
                  <Label>Nome do Parceiro</Label>
                  <Input
                    data-testid="partner-name-input"
                    value={formData.partner_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, partner_name: e.target.value }))}
                    placeholder="Nome de exibição"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label>Comissão do Parceiro (%)</Label>
                <Input
                  data-testid="partner-commission-input"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={formData.commission_percentage}
                  onChange={(e) => setFormData(prev => ({ ...prev, commission_percentage: e.target.value }))}
                  placeholder="Ex: 5"
                />
                <p className="text-xs text-gray-500 mt-1">A comissão é liberada 7 dias após o fim da viagem.</p>
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
              <Button onClick={handleSave} className="flex-1 bg-[#003366]">
                <Save className="w-4 h-4 mr-2" />
                {editingCoupon ? 'Atualizar' : 'Criar Cupom'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCoupons;
